<template>
    <div 
        class="voucher-content" 
        :class="{ 'voucher-locked': isLocked, 'voucher-content--disabled': disabled }"
        :style="{ backgroundImage: `url(${voucherImageUrl})` }"
        @click="handleClick"
    >
        <div class="voucher-cost">
            <span class="voucher-cost-text">{{ voucher.point }} POIN</span>
        </div>
        
        <!-- Lock overlay for locked items -->
        <div v-if="isLocked" class="voucher-lock-overlay">
            <div class="lock-icon">
                <span class="lock-icon-mask" aria-label="Locked"></span>
            </div>
        </div>

        <div class="voucher-container">
            <div class="voucher-header">
                <div class="corporate-logo">
                    <img 
                        v-if="getMerchantLogo"
                        :src="getMerchantLogo" 
                        :alt="voucher?.merchant?.name || 'Corporate Logo'"
                    />
                    <div v-else class="logo-placeholder">
                        {{ getInitials }}
                    </div>
                </div>

                <div class="voucher-title-container">
                    <h3 class="voucher-title">
                        {{ voucher.name }}
                    </h3>
                    <div class="voucher-description">
                        <p class="marquee" :data-text="merchantMarqueeText">{{ merchantMarqueeText }}</p>
                    </div>
                </div>
            </div>

            <div class="voucher-dash-line"/>

            <div class="voucher-status">
                <span v-if="!isLocked" class="voucher-status-icon-mask" aria-label="Voucher status"></span>
                <span v-else class="voucher-status-icon-mask locked" aria-label="Locked"></span>
                <span class="voucher-status-text">
                    {{ isLocked ? `Upgrade to ${displayRequiredTierName} to unlock` : (voucher.is_limited === 0 || voucher.stock === -1 || voucher.stock === null || voucher.stock === undefined ? 'Limited Offers available' : `${voucher.stock} Remaining voucher available`) }}
                </span>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    name: 'MemberVoucherContent',
    props: {
        voucher: {
            type: Object,
            required: true,
            default: () => ({
                name: '',
                point: 0,
                stock: 0,
                merchant: {
                    name: '',
                    logo: {
                        image_url: ''
                    }
                },
                branch: {
                    name: ''
                },
                avatar: {
                    image_url: ''
                }
            })
        },
        isLocked: {
            type: Boolean,
            default: false
        },
        requiredTierName: {
            type: String,
            default: ''
        },
        disabled: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        voucherImageUrl() {
            return this.voucher?.resolved_avatar_image_url ||
                   this.voucher?.avatar?.image_url ||
                   (typeof this.voucher?.avatar === 'string' ? this.voucher.avatar : null) ||
                   this.voucher?.default_image_url ||
                   this.voucher?.image_url ||
                   '/img/member-img/offer-example.svg';
        },
        getMerchantLogo() {
            
            // Cek apakah ada logo dari API response
            let logo = this.voucher?.merchant?.logo?.image_url || 
                      this.voucher?.merchant?.logo ||
                      this.voucher?.corporate?.logo?.image_url ||
                      this.voucher?.corporate?.logo ||
                      this.voucher?.branch?.logo?.image_url ||
                      this.voucher?.branch?.logo ||
                      null;
            
            // Jika tidak ada, coba build URL S3 dari corporate_id atau merchant corporate_id
            if (!logo) {
                const corporateId = this.voucher?.corporate_id || 
                                   this.voucher?.merchant?.corporate_id ||
                                   this.voucher?.branch?.corporate_id;
                
                if (corporateId) {
                    const s3BaseUrl = 'https://s3.ap-southeast-1.amazonaws.com/migration-cdn-member.omnihotelier.com';
                    logo = `${s3BaseUrl}/user_assets/${corporateId}/corporateLogo.png`;
                }
            }
            
            return logo;
        },
        getInitials() {
            const name = this.voucher?.provider_name || this.voucher?.corporate_name || this.voucher?.merchant_name || this.voucher?.merchant?.name || '';
            if (!name) return '?';
            
            const words = name.split(' ');
            if (words.length >= 2) {
                return (words[0][0] + words[1][0]).toUpperCase();
            }
            return name.substring(0, 2).toUpperCase();
        },
        merchantMarqueeText() {
            if (this.voucher?.merchant_scope === 'all_corporate') {
                return 'All Merchants & Branches';
            }
            if (Array.isArray(this.voucher?.eligible_merchants_info) && this.voucher.eligible_merchants_info.length > 1) {
                return `Claimable at ${this.voucher.eligible_merchants_info.length} Locations`;
            }
            if (Array.isArray(this.voucher?.allowed_merchants) && this.voucher.allowed_merchants.length > 1) {
                return `Claimable at ${this.voucher.allowed_merchants.length} Locations`;
            }
            const merchantName = this.voucher?.provider_name || this.voucher?.corporate_name || this.voucher?.merchant_name || this.voucher?.merchant?.name || '';
            const branchName = this.voucher?.branch_name || this.voucher?.branch?.name || '';

            if (merchantName && branchName) {
                return `${merchantName} - ${branchName}`;
            }

            return merchantName || branchName || '';
        },
        displayRequiredTierName() {
            const name = this.requiredTierName;
            const finalName = name && name !== '-' ? name : 'Member';
            return finalName.length > 25 ? finalName.substring(0, 25) : finalName;
        }
    },
    methods: {
        handleClick() {
            if (this.disabled) return;
            this.$emit('click', this.voucher);
        }
    },
}
</script>

<style scoped>
.voucher-content {
    width: 280px;
    min-width: 280px;
    max-width: 280px;
    height: 242px;
    flex-shrink: 0;
    background-size: cover;
    background-position: top;
    background-repeat: no-repeat;
    border-radius: 12px;
    padding: 8px;
    display: flex;
    flex-direction: column;
    justify-content: end;
    position: relative;
    cursor: pointer;
    border: 1px solid #E1E4EA;
    transition: transform 0.2s ease;
}

.voucher-content.voucher-locked {
    opacity: 0.75;
}

.voucher-content.voucher-locked::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.3);
    border-radius: 12px;
    z-index: 1;
}

.voucher-lock-overlay {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 3;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 50%;
    width: 60px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.lock-icon-mask {
    display: inline-block;
    width: 24px;
    height: 24px;
    background-color: var(--primary-color, #F93E3E);
    -webkit-mask-image: url('/img/member-icon/locked-icon.svg');
    -webkit-mask-size: contain;
    -webkit-mask-repeat: no-repeat;
    -webkit-mask-position: center;
    mask-image: url('/img/member-icon/locked-icon.svg');
    mask-size: contain;
    mask-repeat: no-repeat;
    mask-position: center;
    opacity: 0.8;
}

.voucher-status-icon-mask {
    display: inline-block;
    width: 16px;
    min-width: 16px;
    height: 16px;
    background-color: var(--primary-color, #F93E3E);
    -webkit-mask-image: url('/img/member-icon/voucher-status.svg');
    -webkit-mask-size: contain;
    -webkit-mask-repeat: no-repeat;
    -webkit-mask-position: center;
    mask-image: url('/img/member-icon/voucher-status.svg');
    mask-size: contain;
    mask-repeat: no-repeat;
    mask-position: center;
}

.voucher-status-icon-mask.locked {
    width: 12px;
    min-width: 12px;
    height: 12px;
    opacity: 0.6;
    -webkit-mask-image: url('/img/member-icon/locked-icon.svg');
    mask-image: url('/img/member-icon/locked-icon.svg');
}

.voucher-locked .voucher-container {
    position: relative;
    z-index: 2;
}

.voucher-locked .voucher-status-text {
    color: var(--primary-color, #F93E3E) !important;
    font-weight: 600;
}

.voucher-content--disabled {
    opacity: 0.6;
    cursor: not-allowed !important;
}

.voucher-content--disabled:hover {
    transform: none !important;
}


.voucher-cost {
    display: flex;
    padding: 8px;
    background-color: var(--secondary-color, #FFE6E6);
    border-radius: 6px 0px 0px 6px;
    position: absolute;
    top: 12px;
    right: 0px;
    z-index: 2;
}

.voucher-cost-text {
    font-size: 8px;
    font-weight: bold;
    color: var(--primary-color, #F93E3E);
    line-height: 100%;
}

.voucher-status {
    display: flex;
    gap: 8px;
    justify-content: start;
    align-items: center;
    padding-left: 4px;
    padding-right: 4px;
}

.voucher-status-text {
    font-size: 10px;
    color: black;
}

.voucher-dash-line {
    border: none;
    border-top: 1px dashed #E1E4EA;
    margin: 0 8px;
    position: relative;
}

.voucher-title-container {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
}

.voucher-title {
    font-size: 12px !important;
    color: black !important;
    font-weight: 520 !important;
    margin: 0 !important
}

.voucher-description {
    font-size: 10px;
    color: #717784;
    font-weight: normal;
    margin: 0 !important;
    white-space: nowrap;
    overflow: hidden;
    width: 100%;
}

.voucher-description .marquee {
    display: inline-block;
    position: relative;
    margin: 0 !important;
    white-space: nowrap;
    animation: marquee 15s linear infinite;
    will-change: transform;
}

@keyframes marquee {
    0% { transform: translateX(0); }
    100% { transform: translateX(-100%); }
}

.voucher-description .marquee::after {
    content: attr(data-text);
    position: absolute;
    left: 100%;
    top: 0;
    white-space: nowrap;
    padding-left: 24px;
}

.voucher-container {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 12px;
    background: 
        radial-gradient(circle at 0 60px, transparent 12px, #E1E4EA 12px, #E1E4EA 13px, white 13px) left top,
        radial-gradient(circle at 100% 60px, transparent 12px, #E1E4EA 12px, #E1E4EA 13px, white 13px) right top;
    background-size: 51% 100%;
    background-repeat: no-repeat;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    overflow: hidden;
}

.voucher-header {
    display: flex;
    gap: 8px;
    width: 100%;
    padding-left: 4px;
    padding-right: 4px;
}

.corporate-logo {
    width: 35px;
    min-width: 35px;
    height: 35px;
    min-height: 35px;
    background-color: white;
    border-radius: 50%;
    border: 1px solid #E1E4EA;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;
}

.corporate-logo img {
    width: 100%;
    height: fit-content;
    /* object-fit: cover; */
}

.logo-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    font-weight: bold;
    color: white;
    background-color: var(--primary-color, #F93E3E);
}
</style>
